import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function ContentTab({ site }) {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("published");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get(`/articles?site_id=${site.id}&status=${statusF}&limit=50`).then(r => setItems(r.data.items));
  }, [site.id, statusF, refreshKey]);

  async function processImage(id) {
    setBulkMsg("");
    try {
      const r = await api.post(`/images/article/${id}`, {});
      setBulkMsg(`Image → ${r.data.kind}`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Failed");
    }
  }
  async function runPipeline() {
    setBulkBusy(true); setBulkMsg("Fetching new articles…");
    try {
      const ing = await api.post(`/sources/ingest`, { site_id: site.id, max_items_per_source: 30 });
      setBulkMsg(`Fetched ${ing.data.total_new} new · rewriting with AI…`);
      const rw = await api.post(`/articles/bulk-rewrite`, { site_id: site.id, limit: 50 });
      const q = rw.data.queued ?? rw.data.rewritten ?? 0;
      setBulkMsg(`Fetched ${ing.data.total_new} new · queued ${q} AI rewrites. They move to Ready (or Published if auto-publish is on) as each finishes — refresh in ~1 min.`);
      setTimeout(() => setRefreshKey(k => k + 1), 6000);
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Pipeline run failed");
    } finally { setBulkBusy(false); }
  }
  async function bulkProcess() {
    if (!window.confirm("Re-image every ready/published article on this site per its image policy (stock photo or branded card)? Copied source photos are removed from R2.")) return;
    setBulkBusy(true); setBulkMsg("");
    try {
      const r = await api.post(`/images/reimage`, { site_ids: [site.id], force: true, dry_run: false });
      setBulkMsg(`Re-imaging ${r.data.total} article(s) in the background — refresh in a minute (progress under Settings → Article images).`);
      setTimeout(() => setRefreshKey(k => k + 1), 8000);
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Failed");
    } finally { setBulkBusy(false); }
  }
  async function recheckLogos() {
    setBulkBusy(true); setBulkMsg("");
    try {
      const scan = await api.post(`/articles/scan-placeholder-images`, { site_id: site.id, dry_run: false });
      const rp = await api.post(`/articles/bulk-process-images`, { site_id: site.id, limit: 500, force: true });
      setBulkMsg(`Logo scan cleared ${scan.data.cleaned} · re-checked ${rp.data.total_candidates} (kept ${rp.data.processed}, purged ${rp.data.failed})`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Failed");
    } finally { setBulkBusy(false); }
  }

  async function publishAllReady() {
    if (!window.confirm("Publish ALL 'ready' articles for this site now? They go live on the public site immediately.")) return;
    setBulkBusy(true); setBulkMsg("Publishing ready articles…");
    try {
      const r = await api.post(`/articles/bulk-publish`, { site_id: site.id, limit: 500 });
      setBulkMsg(r.data.published
        ? `Published ${r.data.published} article(s) — they're live now (public site updates within ~1 min).`
        : "There were no 'ready' articles to publish. Run 'Fetch & publish now' first.");
      setStatusF("published");
      setRefreshKey(k => k + 1);
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Publish failed");
    } finally { setBulkBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "ready", "published", "archived", "rejected"].map(s => (
          <button
            key={s}
            onClick={() => setStatusF(s)}
            className={`px-3 py-1 text-xs rounded-sm border ${statusF === s ? "border-white text-white" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
          >{s}</button>
        ))}
        <button
          data-testid="content-run-pipeline"
          onClick={runPipeline}
          disabled={bulkBusy}
          className="ml-auto px-3 py-1 text-xs rounded-sm border border-sky-600 bg-sky-600/10 text-sky-300 hover:bg-sky-600/25 disabled:opacity-50 font-semibold"
          title="Fetch new articles from this site's sources, AI-rewrite them, and auto-publish (if enabled) — runs now instead of waiting for the schedule"
        >{bulkBusy ? "Running…" : "Fetch & publish now"}</button>
        <button
          data-testid="content-publish-all-ready"
          onClick={publishAllReady}
          disabled={bulkBusy}
          className="px-3 py-1 text-xs rounded-sm border border-amber-500 bg-amber-500/10 text-amber-300 hover:bg-amber-500/25 disabled:opacity-50 font-semibold"
          title="Publish every article currently in 'ready' — pushes your rewritten backlog live immediately (use when auto-publish is off)"
        >{bulkBusy ? "Publishing…" : "Publish all ready"}</button>
        <button
          data-testid="content-recheck-logos"
          onClick={recheckLogos}
          disabled={bulkBusy}
          className="px-3 py-1 text-xs rounded-sm border border-rose-700 text-rose-300 hover:bg-rose-900/30 disabled:opacity-50"
          title="Purge source/company logos and re-check every image with the latest detection"
        >{bulkBusy ? "Working…" : "Re-check & purge logos"}</button>
        <button
          data-testid="content-bulk-process-images"
          onClick={bulkProcess}
          disabled={bulkBusy}
          className="px-3 py-1 text-xs rounded-sm border border-emerald-700 text-emerald-300 hover:bg-emerald-900/30 disabled:opacity-50"
        >{bulkBusy ? "Processing…" : "Re-image all (policy)"}</button>
      </div>
      {bulkMsg && <div className="text-xs text-zinc-400 mono">{bulkMsg}</div>}
      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
        {items.length === 0 && <div className="px-4 py-8 text-center text-zinc-500 text-sm">No articles in this status.</div>}
        {items.map(a => {
          const hasImage = Boolean(a.image_url);
          const hasMedia = a.media && a.media.thumb;
          return (
            <div key={a.id} className="px-4 py-3 hover:bg-zinc-900/40 transition-colors flex items-center gap-3">
              {hasMedia && <img src={a.media.thumb} alt="" className="w-12 h-12 object-cover rounded-sm border border-zinc-800" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">{a.title || a.original_title}</div>
                <div className="text-[11px] mono text-zinc-500 mt-0.5 truncate">
                  {a.source_name} · {a.category_slug || "—"} · ${(a.ai_cost_usd || 0).toFixed(4)}
                  {hasMedia ? <span className={`ml-2 ${a.image_source === "stock" ? "text-emerald-500" : a.image_source === "card" ? "text-sky-400" : "text-amber-500"}`} data-testid={`image-source-${a.id}`}>· {a.image_source || "source"} img</span>
                    : hasImage ? <span className="ml-2 text-amber-500">· source img not processed</span>
                    : <span className="ml-2 text-zinc-600">· no img yet</span>}
                </div>
              </div>
              {(a.status === "ready" || a.status === "published") && (
                <button
                  data-testid={`process-image-${a.id}`}
                  onClick={() => processImage(a.id)}
                  title="Generate a new image for this article per the site's image policy"
                  className="text-[11px] px-2 py-1 border border-zinc-700 rounded-sm hover:border-emerald-500"
                >Re-image</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
